import { useCallback, useEffect } from 'react';
import { NetworkIcon } from '#assets/ReactSvg.ts';
import { SelectAsync } from '#components/patterns/forms/select/select-async/SelectAsync.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { useBrowserStorage } from '#hooks/useBrowserStorage.ts';
import { useHostFilters } from '#hooks/useHostFilters.ts';
import { makeAllNetworksQueryOptions } from './SelectNetwork.queries.ts';

export const ALL_NETWORKS_FLAG = 'NONE';

type Props = {
  className?: string;
} & Omit<
  React.ComponentProps<typeof SelectAsync>,
  'getItemsQueryOptions' | 'onChange' | 'children' | 'value' | 'label'
>;

export function SelectNetworkPersisted({ className, ...props }: Props) {
  const { setParams, filters } = useHostFilters();
  const [, persistNetworkId, removeStoredNetworkId] = useBrowserStorage('filterNetworkId', 'session');

  const onNetworkChange = useCallback(
    (networkId: string | undefined) => {
      if (networkId !== filters.networkID) {
        const id = networkId === ALL_NETWORKS_FLAG ? undefined : networkId;
        setParams({
          'filter.networkID': id,
          // Clear cursor when changing filters, otherwise it will be invalid
          cursor: undefined,
        });

        id ? persistNetworkId(id) : removeStoredNetworkId();
      }
    },
    [filters.networkID, setParams, persistNetworkId, removeStoredNetworkId]
  );

  useEffect(() => {
    if (typeof filters.networkID === 'string') {
      persistNetworkId(filters.networkID);
    } else {
      removeStoredNetworkId();
    }
  }, [filters.networkID, persistNetworkId, removeStoredNetworkId]);

  const value = filters.networkID === true ? undefined : (filters.networkID ?? ALL_NETWORKS_FLAG);

  return (
    <SelectAsync
      className={className}
      getItemsQueryOptions={makeAllNetworksQueryOptions()}
      label="Network:"
      visuallyHideLabel={true}
      placeholder="All networks"
      value={value}
      modifyItems={(items) => [{ value: ALL_NETWORKS_FLAG, label: 'All networks' }, ...items]}
      size="small"
      icon={<NetworkIcon width={16} height={16} />}
      variant="secondary"
      portal={true}
      {...props}
    >
      {(items) => (
        <>
          {items.map((itm) => (
            <SelectItem key={itm.value} value={itm.value} onClick={() => onNetworkChange(itm.value)} searchable={false}>
              {itm.label}
            </SelectItem>
          ))}
        </>
      )}
    </SelectAsync>
  );
}
